import HistoryIcon from "@mui/icons-material/History";
import PersonIcon from "@mui/icons-material/Person";
import PhotoIcon from "@mui/icons-material/Photo";
import { Avatar, IconButton } from "@mui/material";
import useExcelUrl from "app/useExcelUrl";
import {
  DataGridV2 as DataGrid,
  DataGridBaseBaseProps,
  DataGridLinkColumn,
  GridColumns,
  PageHeader,
  previewFile,
} from "common";
import getRemoteUri from "common/modules/helpers";
import useDictionaries from "features/dictionaries/useDictionaries";
import useAccess from "features/profile/useAccess";
import I18n from "i18n";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AutoCompleteFilter from "../bookings/AutoCompleteFilter";
import useUserList from "./useUserList";
import { UserItem } from "./userApi";
export interface UserListProps extends Omit<DataGridBaseBaseProps, "id"> {
  showHeader?: boolean;
}

export default function UserList({
  showHeader = true,
  ...props
}: UserListProps) {
  const { params, scope, editUrl, ...service } = useUserList();
  const { hasPermissions, canEditItem } = useAccess();
  const { resolveOption, dictionaries } = useDictionaries();
  const [filter, setFilter] = useState<{ roles?: number[] }>({});
  const [section, setSection] = useState<string | undefined>(() => {
    try {
      const saved = sessionStorage.getItem("grid.users");
      return saved ? JSON.parse(saved)?.scope : undefined;
    } catch {
      return undefined;
    }
  });
  const excelUrl = useExcelUrl(
    "users",
    undefined,
    section ? { scope: section } : undefined,
    false,
  );
  const gridFilter = useMemo(() => {
    const f: any[] = [];

    if (filter?.roles && filter.roles.length > 0) {
      f.push({
        columnField: "role_ids",
        operatorValue: "in",
        value: filter.roles,
      });
    }

    return f;
  }, [filter]);

  function onChange(newFilter: { roles?: number[] }) {
    setFilter(prev => ({ ...prev, ...newFilter }));
  }

  let columns: GridColumns<UserItem> = [
    //{ field: "first_name", headerName: I18n.t("User.attributes.first_name"), hideable: true, flex: 1 },
    //{ field: "last_name", headerName: I18n.t("User.attributes.last_name"), hideable: true, flex: 1 },
    {
      field: "file_id",
      headerName: I18n.t("User.attributes.file_id"),
      renderCell: params2 => {
        //TODO: URL
        const photoUrl = getRemoteUri({
          id: params2.row?.file_id,
          extension: "jpg",
        } as any);
        return (
          <Avatar
            onClick={() => {
              photoUrl &&
                previewFile({
                  url: photoUrl,
                  title: params2.row.name,
                  type: "img",
                });
            }}
            src={photoUrl}
            variant="rounded"
            sx={{
              cursor: "pointer",
              background: "transparent",
              border: theme =>
                `solid 1px ${
                  photoUrl ? theme.palette.primary.light : theme.palette.divider
                }`,
              color: photoUrl ? "primary.main" : "divider",
              width: "25px",
              height: "25px",
            }}
          >
            {!photoUrl && <PhotoIcon />}
          </Avatar>
        );
      },
      width: 60,
      sortable: false,
    },
    {
      field: "name",
      headerName: I18n.t("User.attributes.name"),
      renderCell: params => (
        <DataGridLinkColumn
          href={`${editUrl}${params.row?.id}`}
          params={params}
        />
      ),
    },
    { field: "email", headerName: I18n.t("User.attributes.email"), flex: 1 },
    { field: "phone", headerName: I18n.t("User.attributes.phone"), width: 150 },
    {
      field: "partner_id",
      headerName: I18n.t("User.attributes.partner_id"),
      renderCell: params => params.row.partner?.name || "",
      sortable: false,
    },
    {
      field: "login_at",
      headerName: I18n.t("User.attributes.login_at"),
      type: "datetime",
    },
    {
      field: "role_ids",
      headerName: I18n.t("User.attributes.role_ids"),
      sortable: false,
      headerActions: (
        <AutoCompleteFilter<any>
          filterKey="roles"
          filter={filter}
          onChange={onChange as any}
          options={(dictionaries.roles || []) as any}
          title={I18n.t("User.attributes.role_ids")}
          label={I18n.t("User.attributes.role_ids")}
          getOptionLabel={(option: any) => option.label || option.name || ""}
          getOptionValue={(option: any) => option.value || option.id}
        />
      ),
      renderCell: params => {
        return params.row.role_ids
          .map(role_id => resolveOption("roles", role_id)?.label)
          .join(", ");
      },
    },
    {
      field: "settings",
      headerName: I18n.t("User.attributes.settings"),
      type: "json",
    },
    {
      field: "active",
      headerName: I18n.t("User.attributes.active"),
      type: "boolean",
    },
  ];

  return (
    <>
      {showHeader && (
        <PageHeader avatar={<PersonIcon />} title={I18n.t("User.title")} />
      )}
      <DataGrid
        sections={[
          { value: "colleagues", label: I18n.t("User.sections.colleagues") },
          { value: "partners", label: I18n.t("User.sections.partners") },
        ]}
        onSectionChange={setSection}
        {...service}
        filter={gridFilter}
        columns={columns}
        id="users"
        excelUrl={excelUrl}
        {...props}
        //getHeaderName={field => I18n.t(`User.attributes.${field.replaceAll(".", "_")}`)}
        defaultState={{
          hidden_columns: ["first_name", "last_name"],
        }}
        rowActions={(params, openEdit, refresh) => {
          let actions: any[] = [];
          actions.push(
            <IconButton
              component={Link}
              key="user-history"
              size="small"
              title={I18n.t("History.title")}
              to={`/users/${params.row.id}/activity`}
            >
              <HistoryIcon />
            </IconButton>,
          );
          return actions;
        }}
        editUrl={`${editUrl}:id`}
        canCreate={hasPermissions("user.create")}
        canDelete={hasPermissions(["user.editany", "user.editown"])}
        canEdit={hasPermissions(["user.editany", "user.editown"])}
        canDeleteRow={params => canEditItem("user", params.row)}
        canEditRow={params => canEditItem("user", params.row)}
        //canBatchActions={hasPermissions(["user.editany", "user.editown"])}
        canHandleTrash={hasPermissions(["user.editany", "user.editown"])}
      />
    </>
  );
}
